/**
 * Which colours and sexes each sprite slot offers, in one place.
 *
 * The enums already answer this, but each answer lives under a different name -- `HairColors`
 * keyed by hair type, `RaceColors` keyed by race, `SexShirts` keyed the other way round -- so
 * every caller that wants "the colours of this component" has been rewriting the same switch.
 * There are now three: the descriptor matrix, the registry that replaces the forge's branching,
 * and the derivation tool that builds one from the other. They must agree exactly or the
 * registry's colour fallback stops matching the forge's, so they share this.
 *
 * Deliberately a lookup over the existing enums rather than a copy of them: the enums stay the
 * source of what exists, and adding a colour there still reaches everything.
 */
import {
    ArmorColors,
    CapeColors,
    Color,
    EyesColors,
    HairColors,
    NoseColors,
    PantsColors,
    RaceColors,
    RaceSexes,
    Sex,
    SexCapes,
    SexHair,
    SexPants,
    SexShirts,
    SexShoes,
    SexTypes,
    ShieldColors,
    ShirtColors,
    ShoeColors,
    WeaponColors,
} from "../character/character"
import { BauldronColors } from "../character/character"

export type LpcSlot =
    | "body"
    | "hair"
    | "nose"
    | "eyes"
    | "weapon"
    | "pants"
    | "shirt"
    | "armor"
    | "shoes"
    | "shield"
    | "cape"
    | "bauldron"

export const LpcSlots: readonly LpcSlot[] = [
    "body",
    "hair",
    "nose",
    "eyes",
    "weapon",
    "pants",
    "shirt",
    "armor",
    "shoes",
    "shield",
    "cape",
    "bauldron",
]

const COLOR_TABLES: Record<LpcSlot, Record<string, Color[]>> = {
    body: RaceColors as Record<string, Color[]>,
    hair: HairColors as Record<string, Color[]>,
    nose: NoseColors as Record<string, Color[]>,
    eyes: EyesColors as Record<string, Color[]>,
    weapon: WeaponColors as Record<string, Color[]>,
    pants: PantsColors as Record<string, Color[]>,
    shirt: ShirtColors as Record<string, Color[]>,
    armor: ArmorColors as Record<string, Color[]>,
    shoes: ShoeColors as Record<string, Color[]>,
    shield: ShieldColors as Record<string, Color[]>,
    cape: CapeColors as Record<string, Color[]>,
    bauldron: BauldronColors as Record<string, Color[]>,
}

/**
 * The colours a component offers, in declaration order.
 *
 * Order is load-bearing, not incidental: the first entry is what every slot falls back to when
 * asked for a colour it has no art for, so reordering one of these arrays silently changes what
 * an unrecognised colour renders as.
 */
export const colorsForSlot = (slot: LpcSlot, component: string): Color[] => COLOR_TABLES[slot]?.[component] || []

/**
 * Resolve a requested colour the way every forge function does: the colour if the component has
 * it, otherwise the component's first colour. The single rule that stays in code while the
 * component-to-path table becomes data.
 */
export const resolveColor = (slot: LpcSlot, component: string, color: Color): Color | undefined => {
    const colors = colorsForSlot(slot, component)
    if (colors.length < 1) {
        return undefined
    }
    return colors.includes(color) ? color : colors[0]
}

const SEX_TABLES: Partial<Record<LpcSlot, Record<Sex, readonly string[]>>> = {
    hair: SexHair,
    pants: SexPants,
    shirt: SexShirts,
    shoes: SexShoes,
    cape: SexCapes,
}

/**
 * The sexes a component can actually be worn or worn by.
 *
 * Only some slots restrict this. A race declares its own sexes (there is no female horse), and
 * five clothing slots are split by `Sex*` tables -- `cape-solid` is male-only, `balding` hair is
 * male-only, the twelve long styles are female-only. Everything else applies to both.
 *
 * This is NOT consulted when building a path: the forge happily builds `hair/xlong/male/...` if
 * asked. It is what decides which combinations are worth enumerating, so the matrix does not pin
 * paths for characters the game cannot create.
 */
export const sexesForSlot = (slot: LpcSlot, component: string): Sex[] => {
    if (slot === "body") {
        return RaceSexes[component] || []
    }
    const table = SEX_TABLES[slot]
    if (!table) {
        return [...SexTypes]
    }
    return SexTypes.filter(sex => (table[sex] as readonly string[]).includes(component))
}
